'use client'

import { useAuthState } from '@/app/providers/auth'
import { Carousel, type CarouselNavigationRenderProps } from '@/components/carousel'
import { getCountryCodeFromName } from '@/helpers/multiRegion/countries'
import { SPLITS, useFeatureFlag } from '@/helpers/useFeatureFlag'
import { taxAcronym as getTaxAcronym } from '@/helpers/vatRegions'
import { ChevronLeft, ChevronRight, TrendingUp } from '@mui/icons-material'
import { Box, ButtonBase, Stack, Typography } from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'

import type React from 'react'
import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import type { MenuItem } from '../../domain/menu-item'
import type { MenuVendor } from '../../domain/menu-vendor'
import {
  buildPopularItemsMap,
  MIN_POPULAR_ITEMS,
  type PopularItemRef,
  selectPopularItems,
} from '../../domain/popular-items'
import { useHasActiveFilters } from '../../store/gmFiltersStore'
import { usePopularItemsStore } from '../../store/popularItemsStore'
import { useVendorLocationData } from '../../store/vendorLocationStore'
import { MenuItemCardProvider } from '../menu-item-card'
import { MemoizedPopularItemCard } from './popular-item-card'

type Props = {
  vendor: MenuVendor
  visibleItems: MenuItem[]
  popularItemRefs: PopularItemRef[]
}

const SLIDE_WIDTH = 240

const CAROUSEL_PADDING_Y = 1
const SLIDE_PADDING_Y = 1.5
const NAV_INSET_Y = CAROUSEL_PADDING_Y + SLIDE_PADDING_Y - 0.5
const NAV_RADIUS = 1

export const PopularItemsCarousel: React.FC<Props> = ({
  vendor,
  visibleItems,
  popularItemRefs,
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const authState = useAuthState()
  const flagOn = useFeatureFlag(SPLITS.SHOW_POPULAR_ITEMS_GM, {
    userId: authState.type === 'authenticated' ? authState.user.id : '',
  })
  const { taxAcronym } = useVendorLocationData()
  const setPopularItems = usePopularItemsStore((state) => state.setPopularItems)
  const hasActiveFilters = useHasActiveFilters()

  const fallbackCountry = vendor.locations?.[0]?.country
  const fallbackTaxAcronym = fallbackCountry
    ? getTaxAcronym(getCountryCodeFromName(fallbackCountry))
    : 'GST'

  const items = useMemo(
    () =>
      flagOn
        ? selectPopularItems(popularItemRefs, visibleItems, {
            min: hasActiveFilters ? 1 : MIN_POPULAR_ITEMS,
          })
        : [],
    [flagOn, hasActiveFilters, popularItemRefs, visibleItems],
  )

  const popularItems = useMemo(
    () => buildPopularItemsMap(items, popularItemRefs),
    [items, popularItemRefs],
  )

  useEffect(() => {
    setPopularItems(popularItems)
  }, [popularItems, setPopularItems])

  if (hasActiveFilters || items.length === 0) return null

  const navSharedStyles = {
    alignItems: 'center',
    backgroundColor: alpha(theme.palette.primary.main, 0.3),
    display: 'flex',
    justifyContent: 'center',
    position: 'absolute',
    top: theme.spacing(NAV_INSET_Y),
    bottom: theme.spacing(NAV_INSET_Y),
    width: 48,
    zIndex: 10,
    transition: theme.transitions.create(['opacity', 'background-color'], {
      duration: theme.transitions.duration.shortest,
    }),
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.4),
    },
  } as const

  const renderNavigation = ({
    isBeginning,
    isEnd,
    slideNext,
    slidePrev,
  }: CarouselNavigationRenderProps) => (
    <>
      {!isBeginning && (
        <ButtonBase
          aria-label={t('marketplace:previous', { defaultValue: 'Previous' })}
          focusRipple={false}
          sx={{
            ...navSharedStyles,
            borderBottomRightRadius: theme.spacing(NAV_RADIUS),
            borderTopRightRadius: theme.spacing(NAV_RADIUS),
            left: 0,
          }}
          onClick={slidePrev}>
          <Stack alignItems="center" justifyContent="center" height="100%">
            <ChevronLeft htmlColor={theme.palette.common.black} />
          </Stack>
        </ButtonBase>
      )}
      {!isEnd && (
        <ButtonBase
          aria-label={t('marketplace:next', { defaultValue: 'Next' })}
          focusRipple={false}
          sx={{
            ...navSharedStyles,
            borderBottomLeftRadius: theme.spacing(NAV_RADIUS),
            borderTopLeftRadius: theme.spacing(NAV_RADIUS),
            right: 0,
          }}
          onClick={slideNext}>
          <Stack alignItems="center" justifyContent="center" height="100%">
            <ChevronRight htmlColor={theme.palette.common.black} />
          </Stack>
        </ButtonBase>
      )}
    </>
  )

  return (
    <Box sx={{ mb: 2 }}>
      <Stack direction="row" alignItems="flex-start" gap={1.5} sx={{ mb: 1 }}>
        <Box
          sx={{
            alignItems: 'center',
            backgroundColor: 'primary.main',
            borderRadius: 1.5,
            color: 'primary.contrastText',
            display: 'flex',
            flexShrink: 0,
            height: 36,
            justifyContent: 'center',
            width: 36,
          }}>
          <TrendingUp fontSize="small" />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {t('marketplace:popularItems', { defaultValue: 'Popular items' })}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('marketplace:itemsCount', {
                count: items.length,
                defaultValue: `${items.length} items`,
              })}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {t('marketplace:popularItemsSubtitle', {
              defaultValue: 'A quick place to start with the most ordered items on this menu.',
            })}
          </Typography>
        </Box>
      </Stack>

      <MenuItemCardProvider vendor={vendor} addSource="PopularItemsCarousel">
        <Carousel
          slidesPerView="auto"
          spaceBetween={16}
          slideClassName="popular-item-slide"
          renderNavigation={renderNavigation}
          sx={{
            overflow: 'hidden',
            px: 0.5,
            py: 1,
            '& .swiper-wrapper': { alignItems: 'stretch' },
            '& .popular-item-slide': {
              display: 'flex',
              height: 'auto',
              py: 1.5,
              minWidth: SLIDE_WIDTH,
              maxWidth: SLIDE_WIDTH,
            },
            '& .popular-item-slide > .MuiCard-root': { width: '100%' },
          }}>
          {items.map((item) => (
            <MemoizedPopularItemCard
              key={item.variationId}
              item={item}
              vendor={vendor}
              taxAcronym={taxAcronym ?? fallbackTaxAcronym}
            />
          ))}
        </Carousel>
      </MenuItemCardProvider>
    </Box>
  )
}
